/**
 * A revisão do kickoff pelo lado interno — a metade que faltava da regra da contradição.
 *
 * O motor detecta contradição entre trilhas desde a F5 e a spec §2.3 pede que ela seja
 * "detectada **e apresentada** ao consultor". Só a primeira metade existia: `contradicoes` saía
 * por `GET /api/implantacoes/{id}/kickoff` e nenhuma tela a consumia — quem revisava precisava de
 * `curl`. O caso que originou a regra continuava invisível na prática: a ONB-3114 negou controle
 * de estoque no Força de Vendas e, trinta minutos depois, descreveu controle de estoque por
 * integração no formulário de Estoque. Quem leu a issue não tinha como perceber, e a divergência
 * só apareceu na configuração — que é quando custa caro.
 *
 * **A F7 acrescentou o que fazer com ela.** Até aqui a tela terminava na leitura: o consultor via
 * a contradição e o conserto continuava sendo telefonema, porque a trilha congela no envio. Agora
 * ele tem os dois atos — devolver a trilha com o texto do que precisa mudar, e aceitar as
 * respostas registrando o que faltava.
 *
 * Quatro decisões desta tela:
 *
 * · **A contradição vem primeiro, antes das respostas.** Ela é o motivo de a tela existir. Quem
 *   abre a revisão para conferir uma trilha específica não deveria ter de rolar até encontrá-la.
 *
 * · **O portal não escolhe qual valor vale.** Mostra os dois, com a trilha de cada um, e pronto.
 *   Contradição não tem tabela pelo mesmo motivo que a esteira não tem coluna de etapa: resolver
 *   é leitura humana, e guardar a resolução seria um segundo lugar para contar a mesma história.
 *
 * · **O formulário de pendência nasce dentro da trilha que vai voltar**, mesmo quando o gesto
 *   começou na divergência. É ali que o consultor vê o estado sair de "Enviado" quando confirma,
 *   e ver o efeito no lugar do gesto é o que dispensa recarregar a página para acreditar.
 *
 * · **As respostas são as mesmas da tela do cliente, em modo leitura.** Reaproveitar
 *   `PerguntaNoFormulario` é o que garante que o consultor veja a pergunta com a condicional já
 *   resolvida, a procedência da herança e o anexo ancorado — exatamente o que o cliente viu. Uma
 *   segunda renderização voltaria a poder divergir da primeira.
 *
 * **As respostas ficam em abas, uma por trilha** (spec de 28/09, 6.1). Empilhadas numa coluna só,
 * quem vinha conferir o B2B rolava o cadastro, a infraestrutura e o Força de Vendas inteiros até
 * achá-lo. O que vale para a implantação toda — divergências, aceite, módulos sem perguntas e o
 * resultado da última pendência — continua ACIMA das abas, e não numa aba "Resumo": a
 * divergência cruza duas trilhas e o aceite fecha todas, então escondê-los atrás de uma aba seria
 * desfazer a primeira decisão desta lista. A aba aberta vai na URL (`?trilha=`), para que link e
 * recarga voltem a ela; um link com a âncora de uma pergunta (`#pergunta-…`) abre a aba que a
 * contém e rola até ela, mesmo sem `?trilha=`.
 */

import { useEffect } from "react";
import { AtosDoKickoff } from "../componentes/console/AtosDoKickoff";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { Abas, Aviso, Botao, Cartao, Chip, PainelDaAba } from "../componentes/ui";
import { BarraDeProgresso, PerguntaNoFormulario, ancoraDaPergunta } from "../componentes/kickoff";
import {
  AceiteDoKickoff,
  Contradicoes,
  FormularioDaPendencia,
  useAcaoDaPendencia,
  type AcaoDaPendencia,
} from "../componentes/console";
import { ErroDaApi } from "../nucleo/api";
import { useTitulo } from "../nucleo/titulo";
import { formatarCnpj, formatarData } from "../nucleo/implantacoes";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import {
  PERMISSAO_DE_REVISAR_KICKOFF,
  PERMISSAO_DE_GERAR_PACOTE,
  caminhoDaImplantacao,
  caminhoDoPacote,
} from "../nucleo/console";
import {
  CHIP_DO_ESTADO,
  PARAMETRO_DA_TRILHA,
  ROTULO_DO_ESTADO,
  nomeDaTrilhaNoConsole,
  ordenarParaAsAbas,
  origemDaTrilhaNoConsole,
  situacaoDaTrilha,
  useRevisaoDoKickoff,
  type FormularioDaTrilha,
  type RevisaoDoKickoff as Revisao,
} from "../nucleo/kickoff";
import { useMarcosDoKickoff } from "../nucleo/marcosDoKickoff";
import { ConteudoDoPacote } from "./PacoteDoKickoff";

/* Gesto de escrita que a revisão não tem: com `somenteLeitura` os controles ficam desabilitados e
   o botão de remover e o seletor de anexo nem são desenhados, então nada o chama. */
const SEM_ESCRITA = (): void => undefined;

export function RevisaoDoKickoff() {
  const { id } = useParams();
  const implantacaoId = id ?? "";
  const consulta = useRevisaoDoKickoff(implantacaoId);
  useTitulo("Revisão do kickoff");

  if (consulta.isPending) {
    return (
      <p className="lead" role="status">
        Carregando as respostas…
      </p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroDaApi
        ? consulta.error.message
        : "Não foi possível carregar a revisão agora. Tente de novo em instantes.";
    return (
      <>
        <h1 className="titulo-pagina">Revisão do kickoff</h1>
        <Aviso tom="erro" data-testid="falha-da-revisao">
          {mensagem}
        </Aviso>
        <p className="nota-rodape mt-4">
          <Link to="/admin/implantacoes">Voltar à carteira</Link>
        </p>
      </>
    );
  }

  return <Revisao revisao={consulta.data} />;
}

function Revisao({ revisao }: { revisao: Revisao }) {
  const sessao = useSessao();
  /* Esconder os atos de quem não tem `kickoff:revisar` é cortesia, não segurança: quem nega é o
     serviço. O que ela evita é um formulário inteiro que sempre terminaria em 403. */
  const podeRevisar = podeNaSessao(sessao.data, PERMISSAO_DE_REVISAR_KICKOFF);
  const podeGerarPacote = podeNaSessao(sessao.data, PERMISSAO_DE_GERAR_PACOTE);
  /* Com a K-O no GeoTask, o resumo é só consulta (fica na página dele) e o aceite é o passo 1.
     O resumo da revisão espera o caminho chegar, para não buscar os pacotes à toa. */
  const marcos = useMarcosDoKickoff(revisao.implantacao_id, { habilitado: podeRevisar || podeGerarPacote });
  const modo = marcos.data?.modo;
  const acao = useAcaoDaPendencia(revisao.implantacao_id);
  const nomes = Object.fromEntries(revisao.trilhas.map((trilha) => [trilha.chave, trilha.nome]));
  /* O nome de cada trilha pela chave — é com ele que a herança diz de onde veio a resposta
     ("Resposta trazida de CRM 360", não "de crm_360"). Os módulos sem trilha entram também: a
     herança pode apontar para uma trilha que esta revisão não traz. */
  const nomesDasOrigens: Record<string, string> = {
    ...Object.fromEntries(revisao.modulos_sem_trilha.map((modulo) => [modulo.chave, modulo.nome])),
    ...nomes,
  };
  const enunciados = Object.fromEntries(
    revisao.trilhas.flatMap((trilha) =>
      trilha.perguntas.map((pergunta) => [pergunta.id, pergunta.enunciado]),
    ),
  );

  /* ------------------------------------------------ a aba aberta, lida e escrita na URL */
  const [parametros] = useSearchParams();
  const local = useLocation();
  const navegar = useNavigate();
  const abas = ordenarParaAsAbas(revisao.trilhas);
  const prefixoDaAncora = `#${ancoraDaPergunta("")}`;
  const perguntaDaAncora = local.hash.startsWith(prefixoDaAncora)
    ? decodeURIComponent(local.hash.slice(prefixoDaAncora.length))
    : null;
  /* A pergunta da âncora decide a aba quando existe: é o alvo mais preciso que o link carrega, e
     um link antigo pode trazer só ela. Depois vem `?trilha=`, e por fim a primeira aba. */
  const trilhaDaAncora = perguntaDaAncora
    ? abas.find((trilha) => trilha.perguntas.some((pergunta) => pergunta.id === perguntaDaAncora))
        ?.chave
    : undefined;
  const pedida = parametros.get(PARAMETRO_DA_TRILHA);
  const ativa =
    trilhaDaAncora ?? abas.find((trilha) => trilha.chave === pedida)?.chave ?? abas[0]?.chave ?? "";

  function abrirAba(chave: string) {
    const proximos = new URLSearchParams(parametros);
    proximos.set(PARAMETRO_DA_TRILHA, chave);
    /* A âncora sai junto: ela decidiria a aba por cima do clique. `replace` porque trocar de aba
       não é navegar para outra página — o voltar do navegador leva para fora da revisão. */
    void navegar({ search: `?${proximos.toString()}`, hash: "" }, { replace: true });
  }

  // Chegar pela âncora de uma pergunta é chegar NELA, não no topo da aba.
  useEffect(() => {
    if (!perguntaDaAncora) return;
    document.getElementById(ancoraDaPergunta(perguntaDaAncora))?.scrollIntoView({ block: "start" });
  }, [perguntaDaAncora, ativa]);

  /* O formulário da pendência nasce dentro da trilha que vai voltar. Quando o gesto começa na
     divergência, a aba daquela trilha abre junto — senão o formulário abriria numa aba fechada. */
  function apontarDaDivergencia(trilha: string, perguntaId: string) {
    acao.abrir(trilha, perguntaId);
    if (trilha !== ativa) abrirAba(trilha);
  }

  return (
    <>
      <div className="cabecalho-da-trilha">
        <div>
          <h1 className="titulo-pagina">
            {revisao.organizacao.nome_fantasia ?? revisao.organizacao.razao_social}
          </h1>
          <p className="lead mb-2 numero-tabular">{formatarCnpj(revisao.organizacao.cnpj)}</p>
        </div>
        <Chip estado={revisao.enviadas === revisao.total ? "ok" : "aguardando"}>
          {revisao.enviadas} de {revisao.total} enviadas
        </Chip>
      </div>

      <Contradicoes
        contradicoes={revisao.contradicoes}
        aoApontar={podeRevisar ? apontarDaDivergencia : undefined}
      />

      {/* O resultado do ato aparece na página inteira, e não dentro do cartão que o disparou: a
          trilha devolvida também some do formulário aberto, e um aviso preso ao formulário
          desapareceria junto com ele. */}
      {acao.apontada ? (
        <Aviso tom="sucesso" className="mb-4" data-testid="pendencia-apontada">
          <b>
            Pendência {acao.apontada.referencia} registrada em{" "}
            {nomes[acao.apontada.trilha] ?? acao.apontada.trilha}.
          </b>{" "}
          {acao.apontada.trilha_devolvida
            ? "A trilha voltou para o cliente e aceita resposta de novo."
            : "A trilha ainda não tinha sido enviada — não havia congelamento a desfazer."}{" "}
          O cliente lê, na esteira dele: &ldquo;{acao.apontada.descricao}&rdquo;
        </Aviso>
      ) : null}

      {podeRevisar ? <><AceiteDoKickoff revisao={revisao} modo={marcos.isPending ? null : modo} /><AtosDoKickoff revisao={revisao} podeGerar={podeGerarPacote} /></> : null}

      {revisao.modulos_sem_trilha.length > 0 ? (
        <Aviso tom="atencao" className="mb-4" data-testid="modulos-sem-trilha">
          <b>
            O kickoff não perguntou nada sobre{" "}
            {revisao.modulos_sem_trilha.map((modulo) => modulo.nome).join(", ")}.
          </b>{" "}
          O módulo foi contratado e ainda não tem trilha publicada no catálogo — o cliente
          respondeu tudo o que lhe foi oferecido. Levante esses pontos na reunião de kickoff.
        </Aviso>
      ) : null}

      {revisao.trilhas.length === 0 ? (
        <Aviso tom="marca" data-testid="revisao-sem-trilhas">
          Nenhuma trilha de kickoff foi liberada para esta implantação até agora.
        </Aviso>
      ) : (
        <>
          <Abas
            id="revisao"
            rotulo="Respostas por trilha"
            className="abas--embutida mb-4"
            ativa={ativa}
            aoSelecionar={abrirAba}
            itens={abas.map((trilha) => ({
              chave: trilha.chave,
              rotulo: (
                <>
                  {nomeDaTrilhaNoConsole(trilha)}
                  {/* O mesmo status da tabela da implantação: "Concluído" é enviada com as
                      obrigatórias respondidas (spec de 28/09, 6.2). */}
                  <Chip
                    estado={situacaoDaTrilha(trilha.progresso).chip}
                    data-testid={`status-de-${trilha.chave}`}
                  >
                    {situacaoDaTrilha(trilha.progresso).rotulo}
                  </Chip>
                </>
              ),
            }))}
          />
          {abas.map((trilha) => (
            <PainelDaAba
              key={trilha.chave}
              id="revisao"
              chave={trilha.chave}
              ativa={ativa}
              data-testid={`painel-${trilha.chave}`}
            >
              <TrilhaRevisada
                trilha={trilha}
                acao={acao}
                podeRevisar={podeRevisar}
                enunciados={enunciados}
                nomesDasOrigens={nomesDasOrigens}
              />
            </PainelDaAba>
          ))}
        </>
      )}

      {/* O pacote fica depois das respostas. Além de seguir a ordem natural da revisão, isso
          impede que o carregamento assíncrono do resumo desloque uma pergunta aberta por âncora
          depois que o navegador já rolou até ela. */}
      {/* Só o GeoTask confirmado tira a seção; com os marcos em falha vale o Jira, como na main. */}
      {podeGerarPacote && !marcos.isPending && modo !== "geotask" ? (
        <section className="mb-6" aria-labelledby="titulo-do-resumo-na-revisao">
          <h2 className="titulo-secao" id="titulo-do-resumo-na-revisao">
            Resumo e tarefa de Kick Off
          </h2>
          <p className="lead mb-3">
            Depois do aceite, gere o resumo e crie a tarefa sem sair desta revisão.
          </p>
          <ConteudoDoPacote implantacaoId={revisao.implantacao_id} modo="jira" />
        </section>
      ) : null}

      <p className="nota-rodape mt-4">
        <Link to={caminhoDaImplantacao(revisao.implantacao_id, "kickoff")}>Voltar à implantação</Link> ·{" "}
        <Link to={caminhoDoPacote(revisao.implantacao_id)}>Resumo do Kick Off</Link> ·{" "}
        <Link to="/admin/implantacoes">Voltar à carteira</Link>
      </p>
    </>
  );
}

function TrilhaRevisada({
  trilha,
  acao,
  podeRevisar,
  enunciados,
  nomesDasOrigens,
}: {
  trilha: FormularioDaTrilha;
  acao: AcaoDaPendencia;
  podeRevisar: boolean;
  enunciados: Record<string, string>;
  nomesDasOrigens: Record<string, string>;
}) {
  const enviada = formatarData(trilha.sessao.enviada_em);
  const respondidas = trilha.perguntas.filter(
    (pergunta) => pergunta.visivel && pergunta.resposta !== null,
  );
  const apontando = acao.alvo?.trilha === trilha.chave;

  return (
    <Cartao
      className="mb-4"
      titulo={nomeDaTrilhaNoConsole(trilha)}
      data-testid={`trilha-${trilha.chave}`}
      acessorio={
        /* O status ("Concluído" / "Em aberto") está na aba, logo acima; aqui fica o estado do
           envio, que é o que muda quando uma pendência devolve a trilha. */
        <Chip
          estado={CHIP_DO_ESTADO[trilha.sessao.estado]}
          data-testid={`estado-de-${trilha.chave}`}
        >
          {ROTULO_DO_ESTADO[trilha.sessao.estado]}
        </Chip>
      }
    >
      <p className="cartao__texto">
        {origemDaTrilhaNoConsole(trilha.origem)}
        {trilha.sessao.responsavel ? ` · Respondendo: ${trilha.sessao.responsavel.nome}` : ""}
        {enviada ? ` · Enviada em ${enviada}` : ""}
      </p>

      <BarraDeProgresso progresso={trilha.progresso} />

      {podeRevisar ? (
        apontando ? (
          <FormularioDaPendencia
            /* A chave zera o texto ao trocar de alvo: o rascunho escrito para uma trilha não pode
               reaparecer no formulário de outra. */
            key={`${acao.alvo?.trilha}|${acao.alvo?.perguntaId ?? ""}`}
            acao={acao}
            enunciadoDaPergunta={
              acao.alvo?.perguntaId ? enunciados[acao.alvo.perguntaId] : undefined
            }
          />
        ) : (
          <div className="acoes-do-console mt-3">
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              data-testid="apontar-pendencia"
              onClick={() => acao.abrir(trilha.chave)}
            >
              Apontar pendência ao cliente
            </Botao>
          </div>
        )
      ) : null}

      {respondidas.length === 0 ? (
        <p className="cartao__texto mt-3" data-testid={`sem-respostas-${trilha.chave}`}>
          Nada respondido nesta trilha ainda.
        </p>
      ) : (
        <div className="formulario-da-trilha mt-3">
          {respondidas.map((pergunta) => (
            <PerguntaNoFormulario
              key={pergunta.id}
              pergunta={pergunta}
              valor={pergunta.resposta?.valor ?? null}
              somenteLeitura
              ocupado={false}
              nomeDaOrigem={
                pergunta.resposta?.herdada_de_trilha
                  ? nomesDasOrigens[pergunta.resposta.herdada_de_trilha]
                  : undefined
              }
              /* A revisão não escreve: os três gestos existem no contrato do componente e aqui
                 não têm para onde ir. Reaproveitar a mesma renderização é o que garante que o
                 consultor veja exatamente a pergunta que o cliente viu. */
              aoMudar={SEM_ESCRITA}
              aoAnexar={SEM_ESCRITA}
              aoRemoverAnexo={SEM_ESCRITA}
            />
          ))}
        </div>
      )}
    </Cartao>
  );
}
